import React, { useState, useEffect } from 'react';
import { BsTextParagraph } from "react-icons/bs";
import { FaCheckCircle, FaRegTimesCircle, FaTrophy, FaInfoCircle } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import TeamInfoModal from './teaminfo';
import ReactMarkdown from 'react-markdown';

function Leaderboard() {
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
    const [isTeamInfoModalOpen, setIsTeamInfoModalOpen] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Fetch leaderboard data from the backend
    const fetchLeaderboardData = async () => {
        try {
            setIsLoading(true);
            const response = await fetch('/api/get_leaderboard/');
            
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const data = await response.json();
            setLeaderboardData(data.leaderboard);
            setLastUpdated(new Date());
            setError(null);
        } catch (error) {
            console.error("Error fetching leaderboard data:", error);
            setError("Failed to load leaderboard data. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch leaderboard data from the backend every 20 seconds
    useEffect(() => {
        fetchLeaderboardData();
        const interval = setInterval(fetchLeaderboardData, 20000);

        return () => clearInterval(interval);
    }, []);

    // Open modal with the selected description
    const openDescriptionModal = (description) => {
        setSelectedDescription(description);
        setIsDescriptionModalOpen(true);
        // Prevent body scrolling when modal is open
        document.body.style.overflow = 'hidden';
    };

    // Close the description modal
    const closeDescriptionModal = () => {
        setIsDescriptionModalOpen(false);
        setSelectedDescription('');
        // Re-enable body scrolling
        document.body.style.overflow = 'auto';
    };

    // Open modal with the selected team info
    const openTeamInfoModal = (team_id) => {
        setSelectedTeam(team_id);
        setIsTeamInfoModalOpen(true);
        // Prevent body scrolling when modal is open
        document.body.style.overflow = 'hidden';
    };

    // Close the team info modal
    const closeTeamInfoModal = () => {
        setIsTeamInfoModalOpen(false);
        setSelectedTeam(null);
        // Re-enable body scrolling
        document.body.style.overflow = 'auto';
    };

    // Handle ESC key press for modals
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                if (isDescriptionModalOpen) closeDescriptionModal();
                if (isTeamInfoModalOpen) closeTeamInfoModal();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isDescriptionModalOpen, isTeamInfoModalOpen]);

    // Enhanced custom renderers with expandable sections
    const customRenderers = {
        img: ({node, ...props}) => (
            <img {...props} className="max-w-full md:max-w-[70%] h-auto my-4 mx-auto rounded-md shadow-md" loading="lazy" />
        ),
        p: ({node, ...props}) => (
            <p {...props} className="my-3 leading-relaxed" />
        ),
        h1: ({node, children, ...props}) => (
            <h1 {...props} className="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-100 border-b border-gray-300 dark:border-gray-600 pb-2">
                {children}
            </h1>
        ),
        h2: ({node, children, ...props}) => (
            <h2 {...props} className="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-gray-100">
                {children}
            </h2>
        ),
        h3: ({node, children, ...props}) => (
            <h3 {...props} className="text-xl font-semibold mt-5 mb-3 text-gray-900 dark:text-gray-100">
                {children}
            </h3>
        ),
        // Add support for HTML details/summary tags that users might write directly
        details: ({node, ...props}) => (
            <details {...props} className="my-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800" />
        ),
        summary: ({node, children, ...props}) => {
            return (
                <summary {...props} className="bg-gray-50 dark:bg-gray-700 px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-medium flex items-center select-none">
                    <span className="mr-2">▶</span>
                    <span className="text-gray-900 dark:text-gray-100">{children}</span>
                </summary>
            );
        },
        ul: ({node, ...props}) => (
            <ul {...props} className="list-disc pl-6 my-4 space-y-1" />
        ),
        ol: ({node, ...props}) => (
            <ol {...props} className="list-decimal pl-6 my-4 space-y-1" />
        ),
        li: ({node, ...props}) => (
            <li {...props} className="ml-2 mb-1.5" />
        ),
        pre: ({node, ...props}) => (
            <pre {...props} className="bg-gray-100 dark:bg-gray-800 p-4 rounded-md overflow-x-auto my-4 text-sm" />
        ),
        code: ({node, inline, ...props}) => (
            inline 
                ? <code {...props} className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono" />
                : <code {...props} className="block font-mono" />
        ),
        a: ({node, ...props}) => (
            <a {...props} className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer" />
        ),
        table: ({node, ...props}) => (
            <div className="overflow-x-auto my-4">
                <table {...props} className="min-w-full border dark:border-gray-700 rounded-md" />
            </div>
        ),
        thead: ({node, ...props}) => (
            <thead {...props} className="bg-gray-100 dark:bg-gray-800" />
        ),
        th: ({node, ...props}) => (
            <th {...props} className="py-2 px-4 border-b dark:border-gray-700 text-left font-semibold" />
        ),
        td: ({node, ...props}) => (
            <td {...props} className="py-2 px-4 border-b dark:border-gray-700" />
        ),
    };

    // Function to preprocess markdown and group content under headings
    const preprocessMarkdownForSections = (markdown) => {
        const lines = markdown.split('\n');
        const sections = [];
        let currentSection = null;
        let beforeFirstHeading = [];

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

            if (headingMatch) {
                const level = headingMatch[1].length;
                const title = headingMatch[2];

                // Save content before first heading
                if (currentSection === null && beforeFirstHeading.length > 0) {
                    sections.push({
                        type: 'content',
                        content: beforeFirstHeading.join('\n')
                    });
                }

                // Save previous section
                if (currentSection) {
                    sections.push(currentSection);
                }

                // Start new section
                currentSection = {
                    type: 'heading',
                    level: level,
                    title: title,
                    content: []
                };
            } else {
                if (currentSection === null) {
                    beforeFirstHeading.push(line);
                } else {
                    currentSection.content.push(line);
                }
            }
        }

        // Add the last section
        if (currentSection) {
            sections.push(currentSection);
        }

        return sections;
    };

    // Enhanced renderContent function
    const renderContent = (description) => {
        const sections = preprocessMarkdownForSections(description);

        return (
            <div>
                {sections.map((section, index) => {
                    if (section.type === 'content') {
                        // Regular content (not under a heading)
                        const content = section.content;
                        if (content.includes("<img")) {
                            const parts = content.split(/(<img.*?>)/g);
                            return (
                                <div key={index}>
                                    {parts.map((part, partIndex) => {
                                        if (part.startsWith("<img")) {
                                            return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                        } else if (part.trim()) {
                                            return <ReactMarkdown key={partIndex} components={customRenderers}>{part}</ReactMarkdown>;
                                        }
                                        return null;
                                    })}
                                </div>
                            );
                        } else {
                            return <ReactMarkdown key={index} components={customRenderers}>{content}</ReactMarkdown>;
                        }
                    } else if (section.type === 'heading') {
                        // Heading with content
                        const contentMarkdown = section.content.join('\n');
                        const isExpandable = section.level === 2 || section.level === 3;

                        if (isExpandable) {
                            // Render as expandable section
                            const sectionClass = section.level === 2 
                                ? "my-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
                                : "my-4 border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-800";
                            
                            const summaryClass = section.level === 2
                                ? "bg-gray-50 dark:bg-gray-700 px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-semibold text-lg flex items-center select-none"
                                : "bg-gray-100 dark:bg-gray-700 px-3 py-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium flex items-center select-none";
                            
                            const contentClass = section.level === 2 
                                ? "px-4 py-3 bg-white dark:bg-gray-800"
                                : "px-3 py-2 bg-white dark:bg-gray-800";

                            return (
                                <details key={index} className={sectionClass}>
                                    <summary className={summaryClass}>
                                        <span className="expandable-arrow mr-3 transition-transform duration-200">
                                            ▶
                                        </span>
                                        <span className="text-gray-900 dark:text-gray-100">{section.title}</span>
                                    </summary>
                                    <div className={contentClass}>
                                        {contentMarkdown.includes("<img") ? (
                                            (() => {
                                                const parts = contentMarkdown.split(/(<img.*?>)/g);
                                                return parts.map((part, partIndex) => {
                                                    if (part.startsWith("<img")) {
                                                        return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                                    } else if (part.trim()) {
                                                        return <ReactMarkdown key={partIndex} components={{...customRenderers, h2: ({children}) => <h4 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100">{children}</h4>, h3: ({children}) => <h5 className="text-base font-medium mt-3 mb-2 text-gray-900 dark:text-gray-100">{children}</h5>}}>{part}</ReactMarkdown>;
                                                    }
                                                    return null;
                                                });
                                            })()
                                        ) : (
                                            <ReactMarkdown components={{...customRenderers, h2: ({children}) => <h4 className="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-gray-100">{children}</h4>, h3: ({children}) => <h5 className="text-base font-medium mt-3 mb-2 text-gray-900 dark:text-gray-100">{children}</h5>}}>{contentMarkdown}</ReactMarkdown>
                                        )}
                                    </div>
                                </details>
                            );
                        } else {
                            // Regular heading (h1, h4, h5, h6)
                            const headingMarkdown = `${'#'.repeat(section.level)} ${section.title}\n\n${contentMarkdown}`;
                            if (headingMarkdown.includes("<img")) {
                                const parts = headingMarkdown.split(/(<img.*?>)/g);
                                return (
                                    <div key={index}>
                                        {parts.map((part, partIndex) => {
                                            if (part.startsWith("<img")) {
                                                return <div key={partIndex} dangerouslySetInnerHTML={{ __html: part }} className="my-4 text-center" />;
                                            } else if (part.trim()) {
                                                return <ReactMarkdown key={partIndex} components={customRenderers}>{part}</ReactMarkdown>;
                                            }
                                            return null;
                                        })}
                                    </div>
                                );
                            } else {
                                return <ReactMarkdown key={index} components={customRenderers}>{headingMarkdown}</ReactMarkdown>;
                            }
                        }
                    }
                    return null;
                })}
            </div>
        );
    };

    // Get trophy icon based on rank
    const getTrophyIcon = (rank) => {
        if (rank === 1) return <FaTrophy className="text-yellow-500" title="1st Place" />;
        if (rank === 2) return <FaTrophy className="text-gray-400" title="2nd Place" />;
        if (rank === 3) return <FaTrophy className="text-amber-700" title="3rd Place" />;
        return null;
    };

    // Format date for last updated timestamp
    const formatLastUpdated = (date) => {
        if (!date) return '';
        
        return new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        }).format(date);
    };

    return (
        <section className="py-8">
            {/* Add CSS styles for details/summary elements */}
            <style jsx>{`
                details > summary {
                    list-style: none;
                }
                details > summary::-webkit-details-marker {
                    display: none;
                }
                details[open] > summary .expandable-arrow {
                    transform: rotate(90deg);
                }
            `}</style>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-secondary-dark p-4 flex items-center justify-between">
                    <h2 className="text-xl md:text-2xl font-bold text-white flex items-center">
                        Scoreboard
                    </h2>
                    
                    {lastUpdated && (
                        <div className="text-xs text-gray-200">
                            Last updated: {formatLastUpdated(lastUpdated)}
                        </div>
                    )}
                </div>
                
                <div className="p-4 md:p-6">
                    {isLoading && leaderboardData.length === 0 ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary"></div>
                            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading scoreboard...</span>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 dark:bg-gray-700 border-l-4 border-red-500 p-4 my-4 rounded">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <FaRegTimesCircle className="h-5 w-5 text-red-500" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                                </div>
                            </div>
                        </div>
                    ) : leaderboardData.length === 0 ? (
                        <div className="text-center py-8">
                            <FaInfoCircle className="mx-auto h-10 w-10 text-gray-400 mb-4" />
                            <p className="text-gray-600 dark:text-gray-400 text-lg">
                                No submissions have been made yet for this competition.
                            </p>
                            <p className="text-gray-500 dark:text-gray-500 mt-2">
                                Check back later for updates or make your submission.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-100 dark:bg-gray-700 text-left text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                        <th className="px-4 py-3 rounded-tl-lg">#</th>
                                        <th className="px-4 py-3">Team</th>
                                        <th className="px-4 py-3">Description</th>
                                        <th className="px-4 py-3 text-right">Score</th>
                                        {leaderboardData.length > 0 && leaderboardData[0].final_score !== undefined && (
                                            <th className="px-4 py-3 text-right">Final Score</th>
                                        )}
                                        <th className="px-4 py-3 text-center rounded-tr-lg">Verified</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {leaderboardData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`
                                                ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}
                                                hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                                                ${item.rank <= 3 ? 'font-medium' : ''}
                                            `}
                                        >
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <span className={`w-6 inline-flex mr-2 ${item.rank <= 3 ? 'text-lg' : ''}`}>
                                                        {item.rank}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                                                    onClick={() => openTeamInfoModal(item.team_id)}
                                                >
                                                    {item.team}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    className="inline-flex items-center justify-center p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                                    onClick={() => openDescriptionModal(item.description)}
                                                    title="View Description"
                                                >
                                                    <BsTextParagraph className="text-gray-600 dark:text-gray-300" />
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold">
                                                {item.score}
                                            </td>
                                            {item.final_score !== undefined && (
                                                <td className="px-4 py-3 text-right font-semibold">
                                                    {item.final_score}
                                                </td>
                                            )}
                                            <td className="px-4 py-3 text-center">
                                                {item.verify ? (
                                                    <span className="inline-flex items-center justify-center p-1" title="Verified">
                                                        <FaCheckCircle className="text-green-500 text-lg" />
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center justify-center p-1" title="Not Verified">
                                                        <FaRegTimesCircle className="text-red-500 text-lg" />
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for showing description */}
            {isDescriptionModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeDescriptionModal}>
                    <div 
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-4 bg-secondary-dark text-white flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Submission Description</h3>
                            <button
                                onClick={closeDescriptionModal}
                                className="text-white hover:bg-gray-700 rounded-full p-1 transition-colors"
                                aria-label="Close"
                            >
                                <IoClose size={24} />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-grow">
                            {/* Render content with mixed HTML and markdown */}
                            <div className="description-content prose dark:prose-invert prose-img:my-2 prose-headings:my-4 max-w-none">
                                {renderContent(selectedDescription)}
                            </div>
                        </div>
                        
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                            <button
                                className="px-4 py-2 bg-secondary hover:bg-secondary-dark text-white rounded-md transition-colors flex items-center"
                                onClick={closeDescriptionModal}
                            >
                                <IoClose className="mr-1" /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for showing team info */}
            {isTeamInfoModalOpen && selectedTeam && (
                <TeamInfoModal team_id={selectedTeam} onClose={closeTeamInfoModal} />
            )}
        </section>
    );
}

export default Leaderboard;